import cleanDeep from 'clean-deep';
import { format } from 'date-fns';
import { parseFromTimeZone } from 'date-fns-timezone';
import _, { uniqBy, intersectionBy } from 'lodash';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import {
  RemoveTags,
  convertStringToNull,
  convertToTitleCase,
  isNumericId,
  isValidDate,
  uuidValidateV4,
  removeCamelCase,
} from '../../utils/GlobalFunctions.js';
import { minorToMajorUnits, stripeCurrencyToSymbol } from '../../utils/stripeCurrency';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { Eye, PlusCircle, Pencil, Link as LinkIcon, Trash2, RefreshCw } from 'lucide-react';

/**
 * Read an add-on product's prices, which `GET /admin/portal/addOnProducts` returns
 * nested under `display_prices` keyed by currency rather than as flat columns.
 *
 * @param {object} product — a row from the add-on products table.
 * @returns {{ currency: string, monthly: number|null, annual: number|null }} Amounts in Stripe minor units.
 */
export const readAddOnProductPrices = (product) => {
  const displayPrices = product?.display_prices || {};
  const currency =
    product?.default_currency || product?.currency || product?.currencies?.[0] || Object.keys(displayPrices)[0] || 'gbp';
  const prices = displayPrices[currency] || {};

  return {
    currency,
    monthly: product?.price_monthly ?? prices.monthly ?? null,
    annual: product?.price_annual ?? prices.annual ?? null,
  };
};

// Some values we don't want to validate as they are not ids or uuids
const titleCasedValue = (dataKey, type, table) => {
  if (type === 'titleCase' && dataKey) {
    // Values we don't want to convert to title case
    const numericValues = ['scope', 'co2e', 'td', 'name', 'factor'];
    const doNotTitleCase = [
      'id',
      'metric',
      'subentity',
      'ratio',
      'email',
      'org',
      'country',
      'category',
      'iso',
      'code',
      'factor',
      'source',
      'type',
      'symbol',
      'description',
      'featurekey',
    ];
    return numericValues.concat(doNotTitleCase).some((x) => dataKey?.toLowerCase().includes(x));
  } else {
    return false;
  }
};

// Filters the data based on the filters object
export function filteringTable(data, filters, stateVariables) {
  // If no filters, return current data
  if (Object.keys(cleanDeep(filters)).length === 0) {
    return data;
  }

  // helper to get stateVariable key
  const table = (part) => {
    if (part === 'entity') return 'entities';
    if (part === 'subentity') return 'subentities';
    return part;
  };

  // build a map of checker functions for each filter key
  const checkers = {};

  Object.keys(filters).forEach((key) => {
    const testValue = String(filters[key]).toLowerCase();
    console.log(testValue);

    if (key === 'addOns') {
      // array of strings
      checkers[key] = (obj) => Array.isArray(obj[key]) && obj[key].some((x) => x === filters[key]);
    } else if (key === 'permissions') {
      checkers[key] = (obj) => Array.isArray(obj[key]?.portal) && obj[key].portal.some((x) => x === filters[key]);
    } else {
      // default handler
      checkers[key] = (obj) => {
        const cell = obj[key];

        // 1) if it’s an array of strings
        if (Array.isArray(cell)) {
          return cell.includes(testValue);
        }

        // 2) if it’s a string, do a substring match
        const cellStr = String(cell ?? '').toLowerCase();
        if (cellStr.includes(testValue)) {
          return true;
        }

        // 3) otherwise assume it’s a composite-ID string, split key & value, then try each part
        // The search bar adds all Ids together in the same string separated by '_' same with the value of the cell so we need to split it up to get each value on its own and then check its name
        const keyParts = key.split('_');
        const idParts = String(cell ?? '').split('_');

        for (let i = 0; i < keyParts.length; i++) {
          const key = keyParts[i];
          const id = idParts[i];
          if (!id) continue;

          const listName = table(key);
          const list = stateVariables?.[listName];
          if (!Array.isArray(list)) continue;

          // find the matching object by id
          const match = list.find((item) => String(item.id) === id);
          if (
            match &&
            String(match.name ?? '')
              .toLowerCase()
              .includes(testValue)
          ) {
            return true;
          }
        }

        // 4) nothing matched
        return false;
      };
    }
  });

  // finally, filter the data
  return data.filter((obj) => Object.keys(filters).every((key) => checkers[key](obj)));
}

// Formats the way the user can interact with the table
export const userActions = ({
  rowData,
  table,
  tableActions,
  toggleModal,
  history,
  dataKey,
  label,
  getItemsByTermsFromIndex,
  stateVariables,
  searchableFields,
  setCurrentOrganisation,
}) => {
  let optionType;
  if (table === 'usageTypes') {
    // Need to get the type from the url path, for this specific table
    optionType = window.location.pathname.split('/')[2]?.replace('Type', '');
  }

  if (dataKey === 'viewData') {
    return (
      <div className='flex justify-center'>
        <Tooltip>
          <TooltipTrigger asChild>
            <Eye
              size={16}
              strokeWidth={1.5}
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                if (label === 'Conversion Factors') {
                  await getItemsByTermsFromIndex('conversionFactors', { addOns: rowData.id }, 1, 1000);
                }
                toggleModal(true, `show-${table}-${_.camelCase(label)}`, { ...rowData, stateVariables: stateVariables });
              }}
            />
          </TooltipTrigger>
          <TooltipContent>{`View ${label}`}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className='flex justify-center'>
      {tableActions?.map((action, index) => {
        const label = action?.label || action;
        const userAction = action?.action || action;
        const tooltip = action?.tooltip;
        const modalAction = action?.modalAction;
        return (
          <div key={index} style={{ padding: 7 }}>
            {userAction === 'addTo' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <PlusCircle
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleModal(true, `add-${table}-to-Organisation`, rowData)}
                  />
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'editSBT' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Pencil
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      let link = `/netZero/${rowData.id}`;
                      const orgDetails = _.find(stateVariables?.['adminOrgs'], { id: rowData.organisation });
                      setCurrentOrganisation(orgDetails);
                      history.push(link);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'viewOrg' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Eye
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      let link = `/organisations/${rowData.id}`;
                      if (table === 'benchmark') {
                        link = `/organisations/${rowData.org}`;
                      } else if (table === 'organisationMetrics') {
                        link = `/organisations/${rowData.organisationId}`;
                      }
                      history.push(link);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>{tooltip || 'View Organisation'}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'link' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <LinkIcon
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      let link;
                      if (table === 'reports') {
                        link = `/report/${rowData.organisationId}/${rowData.id}`;
                      }
                      history.push(link);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>{`Go to ${convertToTitleCase(table)}`}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'edit' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Pencil
                    size={20}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (table === 'subentity') {
                        rowData.reportSections = stateVariables?.['secrMappings']?.filter((x) => x.subentity === rowData.id);
                        toggleModal(true, `${optionType || table}-table-edit`, rowData);
                      } else {
                        toggleModal(true, `${optionType || table}-table-edit`, rowData);
                      }
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>{`Edit ${convertToTitleCase(table)}`}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'delete' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Trash2
                    size={20}
                    strokeWidth={1.5}
                    className='font-normal'
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleModal(true, `${optionType || table}-table-delete`, rowData, false)}
                  />
                </TooltipTrigger>
                <TooltipContent>{`Delete ${convertToTitleCase(table)}`}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'recalculate' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <RefreshCw
                    id='recalcOrgUsage'
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleModal(true, 'orgs-table-recalcOrgUsage', rowData)}
                  />
                </TooltipTrigger>
                <TooltipContent>{`Recalculate by ${convertToTitleCase(table)}`}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'clearCache' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <RefreshCw
                    id='recalcOrgUsage'
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleModal(true, modalAction, rowData)}
                  />
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            )}
            {userAction === 'syncPrices' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <RefreshCw
                    size={16}
                    strokeWidth={1.5}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleModal(true, 'addOnProducts-sync-prices', rowData)}
                  />
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Formats the way the data is displayed in the table
export const formatCellValue = ({
  cellData,
  rowData,
  dataKey,
  table,
  stateVariables,
  tableActions,
  history,
  toggleModal,
  label,
  getItemsByTermsFromIndex,
  searchableFields,
  setCurrentOrganisation,
}) => {
  // Check for 'null' values
  let value = convertStringToNull(cellData);
  let stateProperty = dataKey;
  let property = 'name';
  let searchUserFirstAndLastName = false;

  const STATE_MAPPINGS = {
    org: ['whiteLabelAdmin', 'consultantAdmin', 'organisation', 'org', 'organisationId', 'owner'],
    metric: ['intensityRatioMetric1ID', 'intensityRatioMetric2ID', 'intensityRatioMetric3ID'],
    industries: ['parent', 'industry'],
    electricitySources: ['source'],
    subentities: ['subentity', 'subentityId'],
    entities: ['entity', 'entityId'],
    countries: ['country'],
    facilityTypes: ['facilityTypeId'],
    usageTypes: ['default'],
  };

  const isDateValue = isValidDate(value);

  // Some values are stored as ID's, so we need to get the name from the stateVariables
  if (!isDateValue) {
    for (const [stateProp, keys] of Object.entries(STATE_MAPPINGS)) {
      if (keys.includes(dataKey)) {
        stateProperty = stateProp === 'org' ? 'adminOrgs' : stateProp;
        if (stateProp === 'electricitySources') property = 'source';
        break;
      }
    }

    if (['userPermissions', 'addOns'].includes(table)) {
      stateProperty = 'profile';
      searchUserFirstAndLastName = true;
    }

    if (stateVariables?.[stateProperty] && value) {
      value = _?.find(stateVariables[stateProperty], { id: cellData })?.[property];
    }
  }

  // For tables we need to convert user id to user first and last name
  if (['creator', 'user'].includes(dataKey) && searchUserFirstAndLastName) {
    const users = stateVariables?.[stateProperty];
    if (users) {
      let user = users.find((x) => x.id === cellData) || users.find((x) => x.cognitoId === cellData);
      value = user ? `${user.firstName} ${user.lastName}` : '';
    }
  }

  // Value is still undefined, so we just display the cellData
  if (!value) value = convertStringToNull(cellData);

  // Date Values needs to be parsed differently and show the date in a different format
  if (isDateValue) value = format(parseFromTimeZone(new Date(value), { timeZone: 'Europe/London' }), 'dd/MM/yyyy');

  if (dataKey === 'price' && stateVariables?.price) value = stateVariables.price.find((x) => x.id === cellData)?.priceType;

  // Add-on prices live in `display_prices` in Stripe minor units; show them as money.
  if (table === 'addOnProducts' && ['price_monthly', 'price_annual'].includes(dataKey)) {
    const prices = readAddOnProductPrices(rowData);
    const major = minorToMajorUnits(dataKey === 'price_monthly' ? prices.monthly : prices.annual);
    return major === '' ? null : `${stripeCurrencyToSymbol(prices.currency)}${major.toFixed(2)}`;
  }

  // Boolean values are rendered as Check/X icons by the table cell renderer
  if (typeof value === 'boolean') return value;

  // Some values are Arrays of data, so we need to map through them and display each item
  if (Array.isArray(value)) {
    cellData.forEach(() => {
      // Check if it's an array of ID's
      if (cellData.some((x) => isNumericId(x) || uuidValidateV4(x))) {
        // Turn it into an Array of Names
        if (table === 'subentity') stateProperty = dataKey === 'entity' ? 'entities' : 'metric';
        if (stateVariables?.[stateProperty]) {
          value = cellData?.map((item) => _.find(stateVariables[stateProperty], { id: item })?.name).filter(Boolean);
        }
      } else value = cellData;
    });

    return Array.isArray(value) ? value.join(', ') : value;
  }

  // Handle special cases
  if (dataKey === 'subentity' && table === 'usageTypeDetails') {
    if (cellData === null || cellData === 'null' || cellData === undefined) return 'All';
  }

  if (table === 'stripe' && dataKey === 'id') return stateVariables?.adminOrgs?.find((o) => o.id === value)?.name || null;

  if (table && dataKey === 'permissions') return value?.portal?.sort((a, b) => a.localeCompare(b));

  if (table === 'userPermissions' && dataKey === 'user') {
    const orgManager = rowData?.permissions?.organisation?.manage;
    return (
      <div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='flex items-center justify-start flex-col overflow-x-auto max-h-[100px] gap-1.5'>
              {value}
              <div className='badge btn-success' style={{ display: orgManager ? 'block' : 'none' }}>
                Organisation Manager
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>{value}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  if (table === 'userPermissions' && dataKey === 'permissions') {
    const permissions = (
      <div style={{ padding: '10px' }}>
        {Object.entries(value).map(([key, options]) => {
          // Special handling for facilities.
          if (key === 'facilities') {
            // Pull out the wildcard permissions if they exist.
            const wildcardOptions = options['*']
              ? Object.keys(options['*'])
                  .filter((opt) => options['*'][opt])
                  .join(', ')
              : '';

            // Get all specific facility keys (exclude the wildcard)
            const specificFacilities = Object.entries(options).filter(([facilityKey]) => facilityKey !== '*');

            return (
              <div key={key}>
                {wildcardOptions && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{`${_.capitalize(key)} *:`}</strong> {wildcardOptions}
                  </div>
                )}

                {specificFacilities.length > 0 && (
                  <div>
                    {specificFacilities.map(([facilityId, perms]) => {
                      const enabled = Object.keys(perms)
                        .filter((p) => perms[p])
                        .join(', ');
                      return (
                        <div key={facilityId} style={{ marginBottom: '8px' }}>
                          <strong>{`Facility (${facilityId}):`}</strong> {enabled}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          } else {
            // For all other permission types, simply list the enabled options.
            const enabledOptions = Object.keys(options)
              .filter((opt) => options[opt])
              .join(', ');
            return (
              <div key={key} style={{ marginBottom: '8px' }}>
                <strong>{_.capitalize(removeCamelCase(key))}:</strong> {enabledOptions}
              </div>
            );
          }
        })}
      </div>
    );

    return (
      <div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='text-start overflow-x-auto max-h-[100px]'>{permissions}</div>
          </TooltipTrigger>
          <TooltipContent>{permissions}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  // Values that should not be converted to title case
  const notTitleCased = titleCasedValue(dataKey, 'titleCase');
  if (!notTitleCased && typeof value === 'string') value = convertToTitleCase(value);

  if(table === 'entity' && dataKey === 'description') {
    return (
      <div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='cursor-pointer'>
              <ReactMarkdown
                remarkPlugins={[gfm]}
                children={cellData?.split('**')[2]}
                className='admin_portal_entities_description'
              />
            </div>
          </TooltipTrigger>
          <TooltipContent className='max-w-xs bg-popover text-popover-foreground border-border'>
            <ReactMarkdown
              remarkPlugins={[gfm]}
              className='admin_portal_entities_description_tooltip'
              children={cellData?.split('**')[2]}
            />
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }
  
  if (['actions', 'viewData'].some((x) => x === dataKey)) {
    return userActions({
      rowData,
      table,
      tableActions,
      toggleModal,
      history,
      dataKey,
      label,
      getItemsByTermsFromIndex,
      stateVariables,
      searchableFields,
      setCurrentOrganisation,
    });
  } else {
    return (
      <div className='cursor-pointer overflow-hidden text-ellipsis admin_portal_cellData_description'>
        {Array.isArray(value) ? (
          value?.map((item, idx) => (
            <Tooltip key={idx}>
              <TooltipTrigger asChild>
                <div className='text-center overflow-x-auto max-h-[100px]'>{item}</div>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs bg-popover text-popover-foreground border-border'>
                {item}
              </TooltipContent>
            </Tooltip>
          ))
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='text-center text-ellipsis whitespace-nowrap overflow-hidden'>{value}</div>
            </TooltipTrigger>
            <TooltipContent className='max-w-xs bg-popover text-popover-foreground border-border'>
              {value}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }
};

// Gets all the Details for each Table: columns to show/hide, actions to perform, sortIndex and tableName
export function getTableDetails(table, profile, stateVariables) {
  // We need the profile details to know what columns to show/hide, depending on the user
  // const { permissions } = profile || {};
  const usageTypes = [
    'seatType',
    'vehicleType',
    'fuelType',
    'productionType',
    'loadType',
    'wasteType',
    'priceType',
    'originlocation',
    'finallocation',
    'factorprovider',
  ];

  let columns = []; // The columns to show on the Admin Table
  let columnsToHide = []; // The columns to hide on the Admin Table, for different users
  let actions = []; // Depending on the table, the actions that can be performed on the table: (edit, delete, etc)
  let sortIndex = 'name'; // The default sortIndex for the table
  let fetchDataFromOtherTables = []; // Some tables need to fetch data from other DB tables in order to show the data
  let tableName = table; // Some table names are different from the url path  -> we need to correct them
  let tableFilters = []; //  Each table can have their own filters
  let tableData = []; // Sometimes we migth not need to fetch the data from the DB, we can just use the data from the state
  let searchableFields = []; // Some tables can be searched by different fields
  let defaultStateValue = {}; // Default State when adding new item

  // Handle all usageTypes in one
  if (usageTypes.some((x) => x === table)) {
    tableName = 'usageTypes';
  }

  console.log();

  switch (tableName) {
    case 'profiles':
      columns = [
        'id', 
        { label: 'first Name', key: 'firstName' }, 
        { label: 'last Name', key: 'lastName' }, 
        'email',
        { label: 'white Label Admin', key: 'whiteLabelAdmin' },
        { label: 'consultant Admin', key: 'consultantAdmin' },
        { label: 'registration Date', key: 'registrationDate' }
      ];
      actions = ['edit', 'exportUserTable'];
      sortIndex = 'firstName';
      tableName = 'profile';
      tableFilters = [
        {
          label: 'White Label Admin',
          options: intersectionBy(
            stateVariables?.['adminOrgs'],
            uniqBy(
              stateVariables?.['profile']
                ?.filter((profile) => profile.whiteLabelAdmin)
                ?.map((profile) => ({ id: String(profile?.whiteLabelAdmin) })),
              'id',
            ),
            'id',
          )?.map((x) => ({ label: x.name, value: x.id })),
          name: 'whiteLabelAdmin',
        },
        {
          label: 'Consultant Admin',
          options: intersectionBy(
            stateVariables?.['adminOrgs'],
            uniqBy(
              stateVariables?.['profile']
                ?.filter((profile) => profile.consultantAdmin)
                ?.map((profile) => ({ id: String(profile?.consultantAdmin) })),
              'id',
            ),
            'id',
          )?.map((x) => ({ label: x.name, value: x.id })),
          name: 'consultantAdmin',
        },
        { label: 'Permissions', options: 'permissions', name: 'permissions' },
      ];
      searchableFields = ['firstName', 'lastName', 'id', 'email'];
      break;
    case 'entities':
      columns = [
        'id',
        'name',
        'metric',
        'description',
        { label: 'Scope 3', key: 'scope_3_cat' },
        { label: 'treat All As Scope 3', key: 'treat_scope_3' },
        'secr',
      ];
      tableName = 'entity';
      actions = ['edit', 'delete', 'downloadFile', 'add'];
      searchableFields = ['id', 'name', 'metric'];
      break;
    case 'industries':
      columns = [
        'id',
        'name',
        { label: 'ISIC Code', key: 'code' },
        'parent',
        'uid',
        { label: 'Description of Non-Standard Ratio', key: 'descriptionOfNonStandardRatio' },
        { label: 'intensity Ratio Metric1 Id', key: 'intensityRatioMetric1ID' },
        { label: 'intensity Ratio Metric2 Id', key: 'intensityRatioMetric2ID' },
        { label: 'intensity  Ratio Metric3 Id', key: 'intensityRatioMetric3ID' },
        'include',
      ];
      actions = ['edit', 'delete', 'add'];
      tableFilters = [
        { label: 'Itensity Ratio Metric 1', options: 'metric', name: 'intensityRatioMetric1ID' },
        { label: 'Itensity Ratio Metric 2', options: 'metric', name: 'intensityRatioMetric2ID' },
        { label: 'Itensity Ratio Metric 3', options: 'metric', name: 'intensityRatioMetric3ID' },
      ];
      searchableFields = ['name', 'id'];
      break;
    case 'industrySubcategory':
      columns = ['id', 'industry', 'name'];
      actions = ['edit', 'delete', 'add'];
      tableFilters = [{ label: 'Industries', options: 'industries', name: 'industry' }];
      fetchDataFromOtherTables = ['industries'];
      searchableFields = ['id', 'name'];
      break;
    case 'userPermissions':
      columns = ['id', { label: 'Organisation', key: 'org' }, 'user', 'email', 'permissions'];
      fetchDataFromOtherTables = ['profile'];
      tableFilters = [{ label: 'Organisation', options: 'adminOrgs', name: 'org' }];
      searchableFields = ['id', 'user', 'email'];
      break;
    case 'reports':
      columns = [
        'id',
        'name',
        { label: 'Organisation', key: 'organisationId' },
        { label: 'start Date', key: 'startDate' },
        { label: 'end Date', key: 'endDate' },
        'year',
        { label: 'factor Year', key: 'factorYear' },
        { label: 'Created At', key: 'created' },
        'status',
      ];
      tableFilters = [{ label: 'Organisation', options: 'adminOrgs', name: 'organisationId' }];
      sortIndex = 'organisationId';
      actions = [
        'link',
        'edit',
        'delete',
        { action: 'clearCache', tooltip: 'Clear Report Cache', modalAction: 'reports-table-cache-delete' },
      ];
      searchableFields = ['name', 'id', 'organisationId', 'description', 'year'];
      break;
    case 'seaDistances':
      columns = [
        'id',
        { label: 'origin Country', key: 'originCountry' },
        { label: 'Origin Country Latitude', key: 'originLat' },
        { label: 'Origin Country Longitude', key: 'originLon' },
        { label: 'origin Port Name', key: 'originPortName' },
        { label: 'origin Port Code', key: 'originPortCode' },
        { label: 'Origin Port Long Code', key: 'originLongCode' },
        { label: 'destination Country', key: 'destinationCountry' },
        { label: 'Destination Country Latitude', key: 'destinationLat' },
        { label: 'Destination Country Longitude', key: 'destinationLon' },
        { label: 'destination Port Name', key: 'destinationPortName' },
        { label: 'destination Port Code', key: 'destinationPortCode' },
        { label: 'Destination Port Long Code', key: 'destinationPortLongCode' },
        { label: 'Distance in Kilometers (KM)', key: 'distanceKm' },
        { label: 'Distance in Miles', key: 'distanceMiles' },
      ];
      tableName = 'sea-distances';
      sortIndex = 'destinationCountryCode';
      actions = ['edit', 'delete', 'add'];
      searchableFields = [
        'id',
        'originCountry',
        'originPortName',
        'originPortLongCode',
        'destinationCountry',
        'destinationPortName',
        'destinationPortLongCode',
      ];
      break;
    case 'airports':
      columns = [
        'code',
        'name',
        'municipality',
        'country',
        { label: 'iso Region', key: 'isoRegion' },
        { label: 'Latitude', key: 'lat' },
        { label: 'Longitude', key: 'lng' },
      ];
      tableName = 'airport';
      actions = ['add', 'edit', 'delete'];
      sortIndex = 'code';
      searchableFields = ['code', 'name', 'municipality', 'country', 'isoRegion'];
      break;
    case 'help':
      columns = ['id', 'title'];
      actions = ['edit', 'delete', 'add'];
      searchableFields = ['id', 'title'];
      break;
    case 'electricitySources':
      columns = ['id', 'renewable', 'source'];
      actions = ['edit', 'delete', 'add'];
      searchableFields = ['id', 'source'];
      break;
    case 'countries':
      columns = [
        'id',
        'name',
        { label: 'country Alpha2 Code', key: 'countryAlpha2Code' },
        { label: 'country Alpha3 Code', key: 'countryAlpha3Code' },
        { label: 'numeric Code', key: 'numericCode' },
        'latitude',
        'longitude',
        'capital',
        { label: 'default Airport', key: 'defaultAirport' },
        'continent',
        { label: 'council Region', key: 'councilRegion' },
        { key: 'currencyName', label: 'Currency' },
      ];
      actions = ['edit', 'delete'];
      searchableFields = ['id', 'name', 'countryAlpha2Code', 'countryAlpha3Code', 'continent', 'councilRegion'];
      fetchDataFromOtherTables = ['currencies'];
      break;
    case 'scope3Categories':
      columns = ['id', { label: 'Category Number', key: 'categoryNo' }, 'name', 'description'];
      actions = ['add', 'edit', 'delete'];
      sortIndex = 'id';
      searchableFields = ['id', 'categoryNo', 'name', 'description'];
      break;
    case 'marketFactors':
      columns = [
        'id',
        'source',
        'subentity',
        'renewable',
        'kgco2e',
        { label: 'kgco2e WTT', key: 'kgco2eWTT' },
        'TD',
        'TDWTT',
        { label: 'radioactive Waste', key: 'radioactiveWaste' },
        'year',
        { label: 'outside Of Scopes', key: 'outsideOfScopes' },
        { label: 'factor Source', key: 'factorSource' },
        { label: 'creation Date', key: 'creationDate' },
        { label: 'modification Date', key: 'modificationDate' },
      ];
      fetchDataFromOtherTables = ['electricitySources', 'addOns', 'usageTypeDetails'];
      actions = ['repackage', 'add', 'edit', 'delete'];
      searchableFields = ['source', 'subentity', 'year', 'id'];
      defaultStateValue =
        Array.isArray(stateVariables?.['usageTypeDetails']) &&
        stateVariables?.['usageTypeDetails'].reduce((acc, ut) => {
          if (['factorprovider', 'originlocation', 'finallocation', 'price'].includes(ut.type)) {
            acc[ut.type] = null;
          }
          return acc;
        }, {});
      tableFilters = [
        { label: 'Subentity', options: 'subentities', name: 'subentity' },
        {
          label: 'Sources',
          options: stateVariables?.['electricitySources']?.map((x) => ({ label: x.source, value: x.id })),
          name: 'source',
        },
        {
          label: 'AddOns',
          options: stateVariables?.['addOns']?.map((x) => ({ label: x.name, value: x.id })),
          name: 'addOns',
        },
      ];
      sortIndex = 'source';
      break;
    case 'subEntities':
      columns = [
        'id',
        'name',
        { label: 'calc market', key: 'calc_market' },
        'description',
        'entity',
        'metric',
        { label: 'add Ons', key: 'addOns' },
        { label: 'hide In UI', key: 'hideInUI' },
      ];
      fetchDataFromOtherTables = ['addOns', 'reportTemplates', 'secrMappings'];
      actions = ['add', 'edit', 'delete', 'recalculate'];
      searchableFields = ['name', 'id'];
      tableName = 'subentity';
      tableFilters = [
        { label: 'Entity', options: 'entities', name: 'entity' },
        { label: 'Metric', options: 'metric', name: 'metric' },
        {
          label: 'AddOns',
          options: stateVariables?.['addOns']?.map((x) => ({ label: x.name, value: x.id })),
          name: 'addOns',
        },
        {
          label: 'Hide in UI',
          options: [
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ],
          name: 'hideInUI',
        },
      ];
      break;
    case 'usageTypes':
      const type = table.replace('Type', '');
      columns =
        type === 'price'
          ? [
              'id',
              { label: 'price Type', key: 'priceType' },
              'description',
              { label: 'hide In Reporting', key: 'hideInReporting' },
            ]
          : ['id', 'name', 'description', { label: 'hide In Reporting', key: 'hideInReporting' }];
      actions = ['add', 'edit', 'delete'];
      searchableFields = type === 'price' ? ['priceType', 'id'] : ['name', 'id'];
      tableFilters = [
        {
          label: 'Hide In Reporting',
          options: [
            { label: 'True', value: 'true' },
            { label: 'False', value: 'false' },
          ],
          name: 'hideInUI',
        },
      ];
      sortIndex = 'name';
      tableData = stateVariables?.[type].map((item) => ({ ...item, description: RemoveTags(item.description) }));
      break;
    case 'metrics':
      columns = [
        'id',
        'name',
        'description',
        { label: 'is Spend', key: 'isSpend' },
        { label: 'currency Id', key: 'currencyId' },
      ];
      actions = ['add', 'edit', 'delete'];
      sortIndex = 'id';
      tableData = stateVariables?.['metric'];
      tableName = 'metric';
      searchableFields = ['id', 'name'];
      break;
    case 'facilities':
      columns = [
        'id',
        'name',
        { label: 'organisation Id', key: 'organisationId' },
        { label: 'post Code', key: 'postCode' },
        { label: 'external Id', key: 'externalId' },
        'country',
        'address',
        { label: 'Facility M2', key: 'facilityM2' },
        { label: 'Primary Facility', key: 'isPrimary' },
        { label: 'Facility Type', key: 'facilityTypeId' },
      ];
      actions = ['edit', 'delete'];
      tableFilters = [
        { label: 'Organisation', options: 'adminOrgs', name: 'organisationId' },
        { label: 'Facility Type', options: 'facilityTypes', name: 'facilityTypeId' },
      ];
      searchableFields = ['name', 'id'];
      break;
    case 'facilitiesType':
      columns = [
        'id',
        'name',
        'description',
        { label: 'cibse Category', key: 'CIBSE_Category' },
        { label: 'Electrical Typical Co2 Benchmark', key: 'Electrical_typical_CO2_benchmark' },
        { label: 'Electricity Typical Benchmark', key: 'Electricity_typical_benchmark' },
        { label: 'Energy Co2e Typical Benchmark', key: 'Energy_C02e_typical_benchmark' },
        { label: 'Extra Intensity Metric', key: 'Extra_Intensity_Metric' },
        { label: 'Fossil-Thermal Co2/m2 benchmark', key: 'Fossil_thermal_CO2_m2_benchmark' },
        { label: 'Fossil-Thermal typical benchmark', key: 'Fossil_thermal_typical_benchmark' },
      ];
      tableName = 'facilityType';
      actions = ['edit', 'delete', 'add'];
      searchableFields = ['name', 'id'];
      break;
    case 'organisationMetrics':
      columns = [
        'id',
        { label: 'organisation', key: 'organisationId' },
        { label: 'start Date', key: 'startDate' },
        { label: 'end Date', key: 'endDate' },
        'year',
        { label: 'metric 1', key: 'metric1' },
        { label: 'metric 2', key: 'metric2' },
        { label: 'metric 3', key: 'metric3' },
        { label: 'original Turnover', key: 'originalTurnover' },
        'turnover',
        'status',
      ];
      actions = ['edit', 'delete', { action: 'viewOrg', tooltip: 'View Organisation Metric' }];
      tableFilters = [{ label: 'Organisation', options: 'adminOrgs', name: 'organisationId' }];
      searchableFields = ['id', 'year'];
      sortIndex = 'organisationId';
      break;
    case 'benchmark':
      columns = [
        'id',
        { label: 'Organisation', key: 'orgName' },
        { label: 'Industry', key: 'industry' },
        { label: 'Parent Industry', key: 'parent' },
        { label: 'Industry Includes', key: 'industryIncludes' },
        'scope1',
        'scope2',
        'scope3',
        { label: 'Scope 1 & 2 Per m', key: 'scope1and2perm' },
        { label: 'Scope 1 & 2 Per m2', key: 'scope1and2perm2' },
        { label: 'Scope 1 & 2 Per fte', key: 'scope1and2perfte' },
        { label: 'Scope 3 Cat', key: 'scope3cat' },
        { label: 'Scope 3 per m', key: 'scope3perm' },
        'year',
      ];
      tableFilters = [
        {
          label: 'Industry',
          options: _.sortBy(stateVariables?.['industries'], ['name'])?.map((x) => ({ label: x.name, value: x.id })),
          name: 'industry',
        },
        {
          label: 'Organisation',
          options: stateVariables?.['adminOrgs']
            ?.filter((x) => x.accountType === 'Benchmark')
            ?.map((x) => ({ label: x.name, value: x.id })),
          name: 'org',
        },
      ];
      searchableFields = ['orgName', 'id', 'year'];
      fetchDataFromOtherTables = ['industries'];
      actions = ['viewOrg', 'edit', 'delete', 'add'];
      break;
    case 'addOns':
      columns = [
        'id',
        'name',
        'creator',
        { label: 'createdDate', key: 'creationDate' },
        { label: 'lastModifiedDate', key: 'modificationDate' },
        'source',
        { label: 'Private', key: 'isPrivate' },
        'owner',
        { label: 'Conversion Factors', key: 'viewData' },
        { label: 'Organisations', key: 'viewData' },
        { label: 'Sub Entities', key: 'viewData' },
        { label: 'stripe Price Id', key: 'stripePriceId' },
      ];
      actions = [
        'edit',
        'delete',
        'add',
        { label: 'Add to Organisation', action: 'addTo' },
      ];
      sortIndex = 'id';
      searchableFields = ['name', 'id', 'owner'];
      fetchDataFromOtherTables = ['profile'];
      defaultStateValue = { creator: profile?.id, isPrivate: false, owner: '621784' };
      break;
    case 'addOnProducts':
      columns = [
        'id',
        'name',
        'description',
        { label: 'Add-On IDs', key: 'addon_ids' },
        { label: 'Price Monthly', key: 'price_monthly' },
        { label: 'Price Annual', key: 'price_annual' },
        'currency',
        { label: 'Min Term', key: 'min_term_months' },
        { label: 'Stripe Monthly Price', key: 'stripe_price_id_monthly' },
        { label: 'Stripe Annual Price', key: 'stripe_price_id_annual' },
        { label: 'createdDate', key: 'creationDate' },
        { label: 'lastModifiedDate', key: 'modificationDate' },
      ];
      actions = [
        'add',
        'edit',
        'delete',
        { label: 'Sync Prices', action: 'syncPrices' },
      ];
      sortIndex = 'name';
      searchableFields = ['name', 'id', 'description'];
      fetchDataFromOtherTables = ['addOns'];
      break;
    case 'usageTypeDetails':
      columns = [
        'id',
        'default',
        { label: 'display Name', key: 'displayName' },
        'subentity',
        'type',
      ];
      actions = ['add', 'edit', 'delete'];
      sortIndex = 'id';
      searchableFields = ['id', 'displayName', 'type'];
      fetchDataFromOtherTables = ['usageTypes'];
      break;
    case 'organisationHierarchy':
      columns = [
        'id',
        { label: 'Parent Organisation', key: 'owner' },
        { label: 'Organisation', key: 'org' },
        'percentage',
        { label: 'start Date', key: 'startDate' },
        { label: 'end Date', key: 'endDate' },
      ];
      sortIndex = 'id';
      searchableFields = ['id', 'org', 'owner'];
      tableFilters = [
        { label: 'Parent Organisation', options: 'adminOrgs', name: 'owner' },
        { label: 'Organisation', options: 'adminOrgs', name: 'org' },
      ];
      fetchDataFromOtherTables = ['adminOrgs'];
      break;
    case 'integrationMappings':
      columns = [
        'id',
        { label: 'Entity', key: 'entityId' },
        { label: 'Subentity', key: 'subentityId' },
        'production',
        'price',
        'seat',
        'factorprovider',
        'originlocation',
        'finallocation',
        'vehicle',
        'fuel',
        'load',
        'waste',
      ];
      actions = ['edit', 'add', 'delete'];
      sortIndex = 'entityId';
      tableFilters = [
        {
          label: 'Entity',
          options: stateVariables?.['entities']?.map((x) => ({ label: x.name, value: x.id })),
          name: 'entityId',
        },
        {
          label: 'Subentity',
          options: stateVariables?.['subentities']?.map((x) => ({ label: x.name, value: x.id })),
          name: 'subentityId',
        },
      ];
      fetchDataFromOtherTables = ['usageTypes'];
      searchableFields = ['id', 'entityId', 'subentityId'];
      break;
    case 'currencies':
      columns = [
        'id',
        'name',
        { label: 'iso Code', key: 'isoCode' },
        'symbol',
        'font:ArialUnicode',
        'unicode:Decimal',
        'unicode:Hex',
        { key: 'hideInUI', label: 'Hide in UI' },
      ];
      actions = ['edit', 'delete', 'add'];
      sortIndex = 'name';
      searchableFields = ['name', 'id', 'isoCode'];
      break;
    case 'features':
      columns = [
        'id',
        'name',
        { label: 'Feature Key', key: 'featureKey' },
        'description',
        { label: 'Enabled By Default', key: 'enabledByDefault' },
        { label: 'Is Beta', key: 'isBeta' },
        { label: 'Last Modified By', key: 'lastModifiedBy' },
        { label: 'Last Modified Date', key: 'modificationDate' },
        { label: 'Organisations', key: 'viewData' },
      ];
      actions = ['edit', 'add', { label: 'Add to Organisation', action: 'addTo' }];
      sortIndex = 'name';
      searchableFields = ['name', 'id', 'featureKey'];
      break;
    case 'currency-rates':
      columns = [
        'id',
        'currency',
        'date',
        { label: 'iso Code', key: 'isoCode' },
        { label: 'from Currency ISO', key: 'fromCurrencyISO' },
        { label: 'to Currency Id', key: 'toCurrencyId' },
        { label: 'to Currency ISO', key: 'toCurrencyISO' },
        'rate',
        { label: 'date Fetched', key: 'dateFetched' },
      ];
      sortIndex = 'isoCode';
      searchableFields = ['id', 'currency', 'toCurrencyId', 'isoCode', 'toCurrencyISO', 'fromCurrencyISO', 'date'];
      break;
    case 'stripe':
      columns = [{ key: 'id', label: 'Organisation' }, { key: 'customerID', label: 'Customer Id' }, 'type'];
      sortIndex = 'id';
      searchableFields = ['id', 'customerID'];
      tableFilters = [{ label: 'Organisation', options: 'adminOrgs', name: 'org' }];
      break;
    case 'secrFuelConversions':
      columns = [
        'id',
        'year',
        'entity',
        'subentity',
        'metric',
        'vehicle',
        'fuel',
        { label: 'kwh Conversion Factor', key: 'kWhConversionFactor' },
      ];
      actions = ['edit', 'delete', 'add'];
      tableFilters = [
        { label: 'Entity', options: 'entities', name: 'entity' },
        { label: 'Subentity', options: 'subentities', name: 'subentity' },
        { label: 'Metric', options: 'metric', name: 'metric' },
        { label: 'Vehicle', options: 'vehicle', name: 'vehicle' },
        { label: 'Fuel', options: 'fuel', name: 'fuel' },
      ];
      sortIndex = 'id';
      searchableFields = ['id', 'entity', 'subentity', 'metric', 'vehicle', 'fuel'];
      break;
    case 'factorSource':
      columns = [
        'id',
        'source',
        { label: 'source And Year', key: 'sourceAndYear' },
        'details',
        { label: 'source Type', key: 'sourceType' },
        { label: 'original Source Url', key: 'originalSourceUrl' },
        'region',
        { label: 'data Type', key: 'dataType' },
        { label: 'data Year', key: 'dataYear' },
        { label: 'publish Year', key: 'publishYear' },
        { label: 'factor Year', key: 'factorYear' },
        'version',
      ];
      actions = ['edit', 'delete', 'add'];
      sortIndex = 'source';
      searchableFields = [
        'source',
        'id',
        'details',
        'sourceType',
        'originalSourceUrl',
        'region',
        'dataType',
        'dataYear',
        'publishYear',
        'factorYear',
      ];
      break;
    case 'orgTargets':
      columns = [
        'id',
        'organisation',
        'type',
        { label: 'base Year', key: 'baseYear' },
        { label: 'target Year', key: 'targetYear' },
        { label: 'base Year Emissions', key: 'baseYearEmissions' },
        { label: 'base Year Output', key: 'baseYearOutput' },
        'scope1',
        'scope2',
        'scope3',
        { label: 'sector Pathway', key: 'sectorPathway' },
        { label: 'setting Method', key: 'settingMethod' },
        { label: 'absolute Reduction', key: 'absoluteReduction' },
        { label: 'target Coverage', key: 'targetCoverage' },
        { label: 'target Year Emissions', key: 'targetYearEmissions' },
      ];
      // actions = ['edit', 'delete'];
      tableFilters = [
        { label: 'Organisation', options: 'adminOrgs', name: 'organisation' },
        {
          label: 'Type',
          options: [
            { label: 'Net Zero', value: 'Net Zero' },
            { label: 'Intensity', value: 'Intensity' },
          ],
          name: 'type',
        },
      ];
      actions = [{ label: 'Edit SBT Targets', action: 'editSBT' }];
      sortIndex = 'organisationId';
      searchableFields = ['id', 'year'];
      break;
    default:
      break;
  }

  // if (permissions?.portal?.includes('EDIT_ADMIN') && actions.length) {
  //   // Add the Actions Column, that allows the user to edit or delete the entry
  //   columns.push('actions');
  // }

  return {
    columns,
    columnsToHide,
    actions,
    sortIndex,
    tableName,
    fetchDataFromOtherTables,
    tableFilters,
    tableData,
    searchableFields,
    defaultStateValue,
  };
}
